// Imports
import {
	faChevronRight,
	faCloudDownload,
	faExclamationTriangle,
	faExternalLink,
	faLock,
	IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { faFacebook, faInstagram, faThreads, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../../../shareables/foundation/front-end/redux/hooks';
import styled, { css } from 'styled-components/macro';
import theme from '../../../../shareables/theme';
import { NavLink, Params, useLocation, useParams } from 'react-router-dom';


// Styled components
const PagesMenuContainer = styled.div`
	h3 {
		padding-bottom: 0.3125rem;
		margin-bottom: -0.2813rem;
		font-size: 1.3125rem;
		color: #222;
		font-weight: 500;
	}
	
	@media only screen and (min-width: 47.9375rem) {
		padding-bottom: 0.625rem;
	}
`;

const PagesList = styled.ul`
	list-style-type: none;
`;

const ListItem = styled.li<{ indent: number }>`
	margin-left: ${({ indent }) => (indent * 30) / 16}rem;
	
	border-bottom: #eee solid 0.0625rem;
	font-size: 0.875rem;
	line-height: 0.875rem;
	text-decoration: none;
	color: #444;
	transition: background 0.3s;
	
	&:hover {
		background: #eee;
	}
	
	a {
		color: #444;
		text-decoration: none;
		overflow-wrap: anywhere;
		
		display: block;
		padding: 0.4375rem 0;
	}
	
	.svg-inline--fa:not(.fa-chevron-right) {
		color: #999;
		padding-left: 0.3125rem;
	}
	
	.svg-inline--fa.fa-facebook {
		color: #3b5998;
	}
	
	.svg-inline--fa.fa-x-twitter {
		color: #000;
	}
	
	.svg-inline--fa.fa-instagram {
		color: #e03664;
	}
	
	.svg-inline--fa.fa-threads {
		color: #000;
	}
	
	.svg-inline--fa.fa-triangle-exclamation {
		color: ${theme.colors.yellow};
	}
	
	.fa-chevron-right {
		padding-right: 0.3125rem;
	}
	
	.active {
		background: #eee;
	}
`;

const Folder = styled.span<{ isOpen: boolean }>`
	cursor: pointer;
	
	display: block;
	padding: 0.4375rem 0;
	
	.fa-chevron-right {
		transition: transform 0.1s;
	}
	
	${(props) =>
		props.isOpen &&
		css`
			font-weight: bold;
			.fa-chevron-right {
				transform: rotate(90deg);
			}
		`}
`;


// Map external links to icon
const externalLinkIcons = {
	facebook: faFacebook,
	twitter: faXTwitter,
	instagram: faInstagram,
	threads: faThreads,
	generic: faExternalLink,
	invalid: faExclamationTriangle,
};


// Function component
const PagesMenu: React.FC = () => {
	// Use state
	const [openFolders, setOpenFolders] = useState<Set<number>>(new Set());
	
	
	// Use Redux functionality
	const pages = useAppSelector((state) => state.self.pages);
	
	
	// Use React Router functionality
	const location = useLocation();
	const { pageIdentifier } = useParams<Params>();
	
	
	// Initialize
	const communityDomain = window.location.hostname;
	
	
	// Automatically expand folders based on current route
	useEffect(() => {
		// Return early if pages hasn't loaded
		if (!pages) {
			return;
		}
		
		
		// Find the index of the page that best matches the location path
		const index = pages.reduce((bestIndex, currentPage, currentIndex) => {
			if (!currentPage.uri) {
				return bestIndex;
			}
			
			const isExactMatch = currentPage.uri === location.pathname;
			const isPrefixMatch = location.pathname.startsWith(`${currentPage.uri}/`);
			
			if (!isExactMatch && !isPrefixMatch) {
				return bestIndex;
			}
			
			if (bestIndex === -1 || (pages[bestIndex].uri?.length ?? 0) < currentPage.uri.length) {
				return currentIndex;
			}
			
			return bestIndex;
		}, -1);
		
		
		// Collapse all folders if no match is found or the route is a top-level page, and return early
		if (index === -1 || pages[index].indent === 0) {
			setOpenFolders(new Set());
			return;
		}
		
		
		// Initialize
		const openParentFolders = new Set<number>();
		let indentLevel = pages[index].indent;
		
		
		// Traverse backwards to find parent folders and mark them as opened
		for (let i = index; i >= 0; i--) {
			// Initialize
			const page = pages[i];
			
			
			// If we've reached the top-level, add the last direct parent folder and stop
			if (page.indent === 0 && page.pageTypeID === 102) {
				// Mark folder as opened
				openParentFolders.add(page.id);
				
				
				// Break
				break;
			}
			
			
			// Check if the current page's indent is less than the current indent level and is a folder
			if (page.indent < indentLevel && page.pageTypeID === 102) {
				// Mark folder as opened
				openParentFolders.add(page.id);
				
				
				// Decrease indent level to avoid adding other folders besides the direct parent folder
				indentLevel--;
			}
		}
		
		
		// Update state
		setOpenFolders(openParentFolders);
	}, [location, pages]);
	
	
	// If loading, then display nothing
	if (pages === null) {
		return null;
	}
	
	
	// Function that handles opening and closing folders
	const toggleFolder = (pageID: number) => {
		setOpenFolders((prev) => {
			const newOpenFolders = new Set(prev);
			
			if (newOpenFolders.has(pageID)) {
				newOpenFolders.delete(pageID);
			} else {
				newOpenFolders.add(pageID);
			}
			
			return newOpenFolders;
		});
	};
	
	
	// Generate page items and its children
	const generatePagesListItems = (startIndex: number, indentLevel: number): JSX.Element[] => {
		// Initialize
		const pageListItems: JSX.Element[] = [];
		
		
		// Iterate over pages
		for (let i = startIndex; i < pages.length; i++) {
			// Initialize
			const page = pages[i];
			
			
			// Stop if we encounter a page with indent lower than the current level
			if (page.indent < indentLevel) {
				break;
			}
			
			
			// Initialize
			const isOpen = openFolders.has(page.id);
			const isFolder = page.pageTypeID === 102 && page.indent === indentLevel;
			
			
			// Check if it's a folder
			if (isFolder) {
				// Recursive call to get children
				const nestedChildren = generatePagesListItems(i + 1, indentLevel + 1);
				
				
				// Add non-empty folders
				if (nestedChildren.length > 0) {
					pageListItems.push(
						<React.Fragment key={page.id}>
							<ListItem key={page.id} indent={indentLevel}>
								<Folder isOpen={isOpen} onClick={() => toggleFolder(page.id)}>
									<FontAwesomeIcon icon={faChevronRight} />
									&nbsp;
									{page.title}
								</Folder>
							</ListItem>
							
							{isOpen && <React.Fragment>{nestedChildren}</React.Fragment>}
						</React.Fragment>
					);
				}
			} else if (page.indent === indentLevel) {
				// Check if Link page type
				if (page.pageTypeID === 100 && page.data && 'url' in page.data) {
					// Initialize
					let icon: IconDefinition | null = null;
					
					
					// Wrap in a try/catch
					try {
						// Initialize
						const url = new URL(page.data.url);
						const externalDomain = url.hostname.split('.').slice(-2).join('.');
						
						
						// Determine which icon to use
						if (externalDomain === 'facebook.com') {
							icon = externalLinkIcons.facebook;
						} else if (externalDomain === 'twitter.com' || externalDomain === 'x.com') {
							icon = externalLinkIcons.twitter;
						} else if (externalDomain === 'instagram.com') {
							icon = externalLinkIcons.instagram;
						} else if (externalDomain === 'threads.net') {
							icon = externalLinkIcons.threads;
						} else if (!url.hostname.includes(communityDomain)) {
							icon = externalLinkIcons.generic;
						}
					} catch {
						// Handle invalid URL
						icon = externalLinkIcons.invalid;
					}
					
					
					// Add link
					pageListItems.push(
						<ListItem key={page.id} indent={indentLevel}>
							<a href={page.data.url} target='_blank' rel='noopener noreferrer'>
								{page.title}
								{icon && (
									<React.Fragment>
										&nbsp;
										<FontAwesomeIcon icon={icon} size={icon === externalLinkIcons.generic ? 'sm' : '1x'} />
									</React.Fragment>
								)}
							</a>
						</ListItem>
					);
					
					
					// Continue
					continue;
				}
				
				
				// Check if Single Document page type
				if (page.pageTypeID === 101 && page.data && 'filename' in page.data) {
					// Add document
					pageListItems.push(
						<ListItem key={page.id} indent={indentLevel}>
							<a
								href={`https://${window.location.hostname}/private-files/document-page/${page.id}/${page.data.filename}/${page.data.friendlyFilename}`}
								className={page.id === parseInt(pageIdentifier ?? '') ? 'active' : undefined}
							>
								{page.title}
								&nbsp;
								<FontAwesomeIcon
									icon={page.hasRestrictions ? faLock : faCloudDownload}
									size={page.hasRestrictions ? 'xs' : 'sm'}
								/>
							</a>
						</ListItem>
					);
					
					
					// Continue
					continue;
				}
				
				
				// Add all other pages
				pageListItems.push(
					<ListItem key={page.id} indent={indentLevel}>
						<NavLink to={`${page.uri ?? ''}`}>
							{page.title}
							{page.hasRestrictions && (
								<React.Fragment>
									&nbsp;
									<FontAwesomeIcon icon={faLock} size='xs' />
								</React.Fragment>
							)}
						</NavLink>
					</ListItem>
				);
			}
		}
		
		
		// Return
		return pageListItems;
	};
	
	
	// Return JSX
	return (
		<PagesMenuContainer>
			<h3>Pages</h3>
			<PagesList>{generatePagesListItems(0, 0)}</PagesList>
		</PagesMenuContainer>
	);
};

export default PagesMenu;
